// GaugeComponent.js
import React, { useRef, useEffect, useState } from "react";

function GaugeComponentInput({ maxValue = 24 }) {
  const canvasRef = useRef(null);
  const [currentValue, setCurrentValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const radius = 100;
  const centerX = 150;
  const centerY = 150;

  // Convert value to angle (-135° to +135° for a nice semi-circle gauge)
  const valueToAngle = (value) => {
    const minAngle = -135;
    const maxAngle = 135;
    const percentage = value / maxValue;
    return minAngle + percentage * (maxAngle - minAngle);
  };

  // Get value from mouse/touch position
  const getValueFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    const dx = x - centerX;
    const dy = y - centerY;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);

    // Convert to our gauge angle range (-135 to 135)
    if (angle < -180) angle += 360;
    if (angle > 180) angle -= 360;

    // Map from -180..180 to -135..135
    let gaugeAngle = angle;
    if (angle < -135) gaugeAngle = -135;
    if (angle > 135) gaugeAngle = 135;

    const minAngle = -135;
    const maxAngle = 135;
    const percentage = (gaugeAngle - minAngle) / (maxAngle - minAngle);
    const value = Math.round(percentage * maxValue);
    return Math.max(0, Math.min(maxValue, value));
  };

  const drawGauge = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw outer arc
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 20;
    ctx.beginPath();
    ctx.arc(
      centerX,
      centerY,
      radius,
      (225 * Math.PI) / 180,
      (315 * Math.PI) / 180
    );
    ctx.stroke();

    // Draw colored segments (optional visual enhancement)
    const angleRad = (valueToAngle(currentValue) * Math.PI) / 180;
    const startRad = (-135 * Math.PI) / 180;
    const endRad = startRad + angleRad;

    // Green to red gradient
    ctx.strokeStyle = currentValue <= maxValue * 0.7 ? "#4CAF50" : "#f44336";
    ctx.lineWidth = 18;
    ctx.beginPath();
    ctx.arc(
      centerX,
      centerY,
      radius,
      (225 * Math.PI) / 180,
      (225 * Math.PI) / 180 + endRad
    );
    ctx.stroke();

    // Draw ticks
    ctx.strokeStyle = "#666";
    ctx.lineWidth = 2;
    for (let i = 0; i <= 10; i++) {
      const tickValue = (i / 10) * maxValue;
      const tickAngle = valueToAngle(tickValue);
      const tickRad = (tickAngle * Math.PI) / 180;

      const x1 = centerX + (radius - 15) * Math.cos(tickRad + Math.PI);
      const y1 = centerY + (radius - 15) * Math.sin(tickRad + Math.PI);
      const x2 = centerX + (radius + 5) * Math.cos(tickRad + Math.PI);
      const y2 = centerY + (radius + 5) * Math.sin(tickRad + Math.PI);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Labels every 5 steps
      if (i % 2 === 0) {
        const labelX = centerX + (radius - 35) * Math.cos(tickRad + Math.PI);
        const labelY = centerY + (radius - 35) * Math.sin(tickRad + Math.PI);
        ctx.font = "12px Arial";
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(tickValue.toFixed(0), labelX, labelY);
      }
    }

    // Draw needle
    const needleAngle = valueToAngle(currentValue);
    const needleRad = (needleAngle * Math.PI) / 180;
    const needleLength = radius - 20;

    ctx.strokeStyle = "#ff4444";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(
      centerX + needleLength * Math.cos(needleRad + Math.PI),
      centerY + needleLength * Math.sin(needleRad + Math.PI)
    );
    ctx.stroke();

    // Center circle
    ctx.fillStyle = "#222";
    ctx.beginPath();
    ctx.arc(centerX, centerY, 12, 0, Math.PI * 2);
    ctx.fill();

    // Value text
    ctx.font = "bold 28px Arial";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(currentValue, centerX, centerY + 50);
  };

  useEffect(() => {
    drawGauge();
  }, [currentValue]);

  const handleStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const value = getValueFromEvent(e);
    setCurrentValue(value);
  };

  const handleMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const value = getValueFromEvent(e);
    setCurrentValue(value);
  };

  const handleEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleEnd);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging]);

  return (
    <div style={{ padding: "20px", background: "#111", borderRadius: "10px" }}>
      <h3 style={{ color: "white", textAlign: "center" }}>
        Drag the needle (0 - {maxValue})
      </h3>
      <canvas
        ref={canvasRef}
        width={300}
        height={300}
        style={{ cursor: "grab", touchAction: "none" }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      />
      <p style={{ color: "white", textAlign: "center" }}>
        Current value: <strong>{currentValue}</strong>
      </p>
    </div>
  );
}

export default GaugeComponentInput;
